import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { useRef } from 'react';
import RouterSideNav from 'ui-addon-react-router-dom/RouterSideNav';
import RouterSideNavItem from 'ui-addon-react-router-dom/RouterSideNavItem';
import { STANDALONE_CORE_SEATS } from '../../../constants/ReleaseInsights';
import { NavItemEnum } from '../../../constants/routing/NavItems';
import { useAddOnsPage } from '../../../hooks/useAddOnPage';
import { useHasReleaseInsight } from '../../../hooks/useBulkPurchaseOptions';
import { useCommerceHub } from '../../../hooks/useCommerceHub';
import { usePageParams } from '../../../hooks/usePageParams';
import { useShouldHideDataOrSuitePage } from '../../../hooks/useShouldHideDataOrSuitePage';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useAppSelector } from '../../../reducers';
import { prefetchPage } from '../../../routes/loadablePages';
import { isUngatedForAddOnsPageV2Selector, isUngatedForHubSpotCreditsPageV2Selector, isUngatedForM1HubPagesSelector, isUngatedForM2BundlePagesSelector, isUngatedForMarketingPlusSelector } from '../../../selectors/GatingSelectors';
import { hasHubSpotCreditsSkuInProductDataSelector } from '../../../selectors/ProductsSelectors';
import { getSegmentedNavItems } from '../../../utils/routingUtils';
import { useIsUngatedForV2PublicHubPages } from '../../../v2/hooks/gating/useIsUngated';
import { useIsFreePortal } from '../../../v2/hooks/inventory/useProductInventory';
import { useIsInAppAuth } from '../../../v2/hooks/user/useUserInfo';
import { usePreserveQueryParams } from '../../../v2/hooks/utilities/usePreserveQueryParams';
import CurrencyPicker from '../../common/CurrencyPicker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SideNavWrapper = ({
  children
}) => {
  var _ref$current;
  const ref = useRef(null);
  const sideNavDomRect = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.getBoundingClientRect();
  // calculates offset of the side nav's bottom from the top of the viewport, accounts for navbar
  const sideNavTop = (sideNavDomRect === null || sideNavDomRect === void 0 ? void 0 : sideNavDomRect.top) || 0;
  const {
    height
  } = useWindowSize();
  const isInAppAuth = useIsInAppAuth();
  return /*#__PURE__*/_jsx(View, {
    position: isInAppAuth ? 'fixed' : 'sticky',
    insetBlockStart: "24px",
    children: /*#__PURE__*/_jsx(ScrollContainer, {
      id: "scroll-container",
      scrollDirection: "vertical"
      // -48 to account for the insetBlockStart=24px of the the two Views
      ,
      maxHeight: `${height - (sideNavTop - 48)}px`,
      overflowY: "auto",
      children: /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "900",
        marginBlockEnd: "1300",
        blockSize: "100%",
        paddingInlineStart: "200",
        children: /*#__PURE__*/_jsx(View, {
          insetBlockStart: "24px",
          children: /*#__PURE__*/_jsx("div", {
            ref: ref,
            children: children
          })
        })
      })
    })
  });
};
const SideNav = ({
  selectedNavItem
}) => {
  const {
    tab
  } = usePageParams();
  const createToWithQueryParams = usePreserveQueryParams();
  const isInAppAuth = useIsInAppAuth();
  const isUngatedForAddOnsPageV2 = useAppSelector(isUngatedForAddOnsPageV2Selector);
  const isUngatedForM1HubPages = useAppSelector(isUngatedForM1HubPagesSelector);
  const isUngatedForM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const isUngatedForHubSpotCreditsPageV2 = useAppSelector(isUngatedForHubSpotCreditsPageV2Selector);
  const isUngatedForV2PublicHubPages = useIsUngatedForV2PublicHubPages();
  const isUngatedToMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  const shouldHideDataOrSuitePage = useShouldHideDataOrSuitePage();
  const {
    hasReleaseInsight: isSmartCrm,
    loading: isLoadingSmartCrm
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const hasHubSpotCreditsSku = useAppSelector(hasHubSpotCreditsSkuInProductDataSelector);
  const {
    showAddonsPage
  } = useAddOnsPage();
  const {
    showCommerceHub
  } = useCommerceHub();
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  const segmentedNavItems = getSegmentedNavItems();
  const shouldHideNavSection = section => {
    return section === 'enhancements' && !showAddonsPage && !hasHubSpotCreditsSku;
  };
  const shouldHideNavItem = item => {
    return item === NavItemEnum.MARKETING_PLUS && !isUngatedToMarketingPlus || item === NavItemEnum.ADD_ONS && !showAddonsPage || item === NavItemEnum.HUBSPOT_CREDITS && !hasHubSpotCreditsSku ||
    // don't show free link if in-app pricing page and paid
    item === NavItemEnum.FREE && isInAppAuth && !isFreePortal && !isInventoryLoading ||
    // don't show bundle link if in-app pricing page
    item === NavItemEnum.BUNDLE && isInAppAuth ||
    //don't show smart crm link if not ungated
    item === NavItemEnum.SMART_CRM && (isLoadingSmartCrm || !isSmartCrm) ||
    // hide commerce hub if experience is disabled
    item === NavItemEnum.COMMERCE && !showCommerceHub ||
    // hide for data hub and suite for pre-seats
    (item === NavItemEnum.DATA || item === NavItemEnum.SUITE) && shouldHideDataOrSuitePage;
  };
  const hasTabs = navItem => {
    return NavItemEnum.BUNDLE !== navItem && NavItemEnum.FREE !== navItem && NavItemEnum.COMMERCE !== navItem && NavItemEnum.MARKETING_PLUS !== navItem && NavItemEnum.ADD_ONS !== navItem && NavItemEnum.HUBSPOT_CREDITS !== navItem;
  };
  const getPath = navItem => {
    const path = `/${navItem}`;
    return hasTabs(navItem) && tab ? `${path}/${tab}` : path;
  };

  // Map nav items to their V2 gating for prefetching the correct page version
  const isV2HubPage = isInAppAuth ? isUngatedForM1HubPages : isUngatedForV2PublicHubPages;
  const getIsV2ForNavItem = navItem => {
    switch (navItem) {
      case 'suite':
      case 'crm':
      case 'bundle':
      case 'marketing-plus':
        return isUngatedForM2BundlePages;
      case 'hubspot-credits':
        return isUngatedForHubSpotCreditsPageV2;
      case 'add-ons':
        return isUngatedForAddOnsPageV2;
      default:
        return isV2HubPage;
    }
  };
  return /*#__PURE__*/_jsxs(SideNavWrapper, {
    children: [Object.keys(segmentedNavItems).map((navSection, index) => {
      if (shouldHideNavSection(navSection)) return null;
      return /*#__PURE__*/_jsx(RouterSideNav, {
        divider: index !== 0,
        headingText: I18n.text(`pricingPage.sidenav.sections.${navSection}`),
        children: segmentedNavItems[navSection].map(navItem => {
          if (shouldHideNavItem(navItem)) return null;
          return /*#__PURE__*/_jsx("span", {
            onMouseEnter: () => prefetchPage(navItem, getIsV2ForNavItem(navItem)),
            children: /*#__PURE__*/_jsx(RouterSideNavItem, {
              "data-test-id": `side-nav-${navItem}`,
              "data-is-active": selectedNavItem === navItem,
              "data-selenium-class": "side-nav",
              active: selectedNavItem === navItem,
              title: /*#__PURE__*/_jsx(Text, {
                textWrap: "nowrap",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `pricingPage.pages.products.tabs.${navItem}.tabTitle`
                })
              }),
              to: createToWithQueryParams(getPath(navItem)),
              padding: "400",
              marginInlineStart: "300"
            })
          }, navItem);
        })
      }, navSection);
    }), /*#__PURE__*/_jsx(CurrencyPicker, {})]
  });
};
export default SideNav;